/**
 * LtvTimeWidget - Виджет LTV по времени
 * =======================================
 * 
 * Показывает как меняется LTV когорт с течением времени
 * с разбивкой по источникам дохода (InApps Android/iOS, AdMon Android/iOS)
 */

import React from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useFilters } from '../../hooks/useFilters';
import { MetricsService } from '../../services/metricsService';
import { formatChartDate, formatTooltipDate, getXAxisConfig } from '../../utils/chartUtils';
import { Card, CardContent } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  BarChart3, 
  LineChart as LineChartIcon, 
  AreaChart as AreaChartIcon,
  Users
} from 'lucide-react';
import { ltv as ltvSpecs } from '@ggstat/chart-specs';

// Ряды и предустановленные дни — общие с MCP (shared/chart-specs/specs/ltv.json);
// ключи COLORS/LABELS — переключатели линий (enabledLines в localStorage)
const SERIES = ltvSpecs.ltvTime.series;
const COLORS = Object.fromEntries(SERIES.map(s => [s.toggle, s.color]));
const LABELS = Object.fromEntries(SERIES.map(s => [s.toggle, s.label]));
const PRESET_DAYS = ltvSpecs.ltvTime.presetDays;

/**
 * Компонент статистики
 */
function StatCard({ icon: Icon, label, value, subtitle }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Кастомная легенда с поддержкой включения/выключения
 */
function CustomLegend({ enabledLines, setEnabledLines }) {
  return (
    <div className="flex justify-center gap-6 mt-4">
      {Object.keys(COLORS).map(key => (
        <div
          key={key}
          onClick={() => setEnabledLines(prev => ({ ...prev, [key]: !prev[key] }))}
          className="flex items-center gap-2 cursor-pointer select-none"
          style={{ opacity: enabledLines[key] ? 1 : 0.4 }}
        >
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: COLORS[key] }}
          />
          <span className="text-sm">{LABELS[key]}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Компонент графика LTV
 */
function LtvChart({ data, chartType = 'line', enabledLines, setEnabledLines, groupBy }) {
  // Получаем настройки оси X
  const xAxisConfig = getXAxisConfig(groupBy, data.length);
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      // Считаем общую сумму для этого периода
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      
      // Фильтруем только ненулевые значения
      const nonZeroPayload = payload.filter(entry => entry.value > 0);
      
      // Форматируем дату для tooltip (полный формат)
      const formattedLabel = formatTooltipDate(label, groupBy);
      
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">{formattedLabel}</p>
          {nonZeroPayload.map((entry, index) => {
            const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0.0';
            return (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {entry.name}: {MetricsService.formatCurrency(entry.value)} ({percentage}%)
              </p>
            );
          })}
          <div className="border-t mt-2 pt-2">
            <p className="text-sm font-bold">
              Total LTV: {MetricsService.formatCurrency(total)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };
  
  if (chartType === 'bar') {
    return (
      <div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="period"
              tickFormatter={(value) => formatChartDate(value, groupBy)}
              {...xAxisConfig}
            />
            <YAxis 
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              tickFormatter={(value) => MetricsService.formatCurrency(value)}
            />
            <Tooltip order="stack" content={<CustomTooltip />} />
            {enabledLines.inappsAndroid && (
              <Bar 
                dataKey="ltvInappsAndroid" 
                name={LABELS.inappsAndroid}
                fill={COLORS.inappsAndroid}
                stackId="ltv"
                isAnimationActive={false}
              />
            )}
            {enabledLines.inappsIos && (
              <Bar 
                dataKey="ltvInappsIos" 
                name={LABELS.inappsIos}
                fill={COLORS.inappsIos}
                stackId="ltv"
                isAnimationActive={false}
              />
            )}
            {enabledLines.admonAndroid && (
              <Bar 
                dataKey="ltvAdmonAndroid" 
                name={LABELS.admonAndroid}
                fill={COLORS.admonAndroid}
                stackId="ltv"
                isAnimationActive={false}
              />
            )}
            {enabledLines.admonIos && (
              <Bar 
                dataKey="ltvAdmonIos" 
                name={LABELS.admonIos}
                fill={COLORS.admonIos}
                stackId="ltv"
                isAnimationActive={false}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
        <CustomLegend enabledLines={enabledLines} setEnabledLines={setEnabledLines} />
      </div>
    );
  }
  
  if (chartType === 'area') {
    return (
      <div>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="period"
              tickFormatter={(value) => formatChartDate(value, groupBy)}
              {...xAxisConfig}
            />
            <YAxis 
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              tickFormatter={(value) => MetricsService.formatCurrency(value)}
            />
            <Tooltip order="stack" content={<CustomTooltip />} />
            {enabledLines.inappsAndroid && (
              <Area 
                type="monotone" 
                dataKey="ltvInappsAndroid" 
                name={LABELS.inappsAndroid}
                stroke={COLORS.inappsAndroid}
                fill={COLORS.inappsAndroid}
                fillOpacity={0.6}
                stackId="ltv"
                isAnimationActive={false}
              />
            )}
            {enabledLines.inappsIos && (
              <Area 
                type="monotone" 
                dataKey="ltvInappsIos" 
                name={LABELS.inappsIos}
                stroke={COLORS.inappsIos}
                fill={COLORS.inappsIos}
                fillOpacity={0.6}
                stackId="ltv"
                isAnimationActive={false}
              />
            )}
            {enabledLines.admonAndroid && (
              <Area 
                type="monotone" 
                dataKey="ltvAdmonAndroid" 
                name={LABELS.admonAndroid}
                stroke={COLORS.admonAndroid}
                fill={COLORS.admonAndroid}
                fillOpacity={0.6}
                stackId="ltv"
                isAnimationActive={false}
              />
            )}
            {enabledLines.admonIos && (
              <Area 
                type="monotone" 
                dataKey="ltvAdmonIos" 
                name={LABELS.admonIos}
                stroke={COLORS.admonIos}
                fill={COLORS.admonIos}
                fillOpacity={0.6}
                stackId="ltv"
                isAnimationActive={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
        <CustomLegend enabledLines={enabledLines} setEnabledLines={setEnabledLines} />
      </div>
    );
  }
  
  // Default: line chart
  return (
    <div>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period"
            tickFormatter={(value) => formatChartDate(value, groupBy)}
            {...xAxisConfig}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => MetricsService.formatCurrency(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          {enabledLines.inappsAndroid && (
            <Line 
              type="monotone" 
              dataKey="ltvInappsAndroid" 
              name={LABELS.inappsAndroid}
              stroke={COLORS.inappsAndroid}
              strokeWidth={2}
              dot={{ fill: COLORS.inappsAndroid, r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
          {enabledLines.inappsIos && (
            <Line 
              type="monotone" 
              dataKey="ltvInappsIos" 
              name={LABELS.inappsIos}
              stroke={COLORS.inappsIos}
              strokeWidth={2}
              dot={{ fill: COLORS.inappsIos, r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
          {enabledLines.admonAndroid && (
            <Line 
              type="monotone" 
              dataKey="ltvAdmonAndroid" 
              name={LABELS.admonAndroid}
              stroke={COLORS.admonAndroid}
              strokeWidth={2}
              dot={{ fill: COLORS.admonAndroid, r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
          {enabledLines.admonIos && (
            <Line 
              type="monotone" 
              dataKey="ltvAdmonIos" 
              name={LABELS.admonIos}
              stroke={COLORS.admonIos}
              strokeWidth={2}
              dot={{ fill: COLORS.admonIos, r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      <CustomLegend enabledLines={enabledLines} setEnabledLines={setEnabledLines} />
    </div>
  );
}

/**
 * Основной компонент виджета
 */
export function LtvTimeWidget() {
  const { platform } = useFilters();
  
  // Используем localStorage для сохранения настроек
  const [groupBy, setGroupBy] = useLocalStorage('ltv-time-widget-groupBy', 'day');
  const [chartType, setChartType] = useLocalStorage('ltv-time-widget-chartType', 'line');
  const [dayFromInstall, setDayFromInstall] = useLocalStorage('ltv-time-widget-dayFromInstall', 7);
  const [customDay, setCustomDay] = React.useState('');
  const [enabledLines, setEnabledLines] = useLocalStorage('ltv-time-widget-enabledLines', {
    inappsAndroid: true,
    inappsIos: true,
    admonAndroid: true,
    admonIos: true
  });
  
  // Предустановленные дни для кнопок
  const presetDays = PRESET_DAYS;
  
  // Читаем состояние чекбокса из localStorage
  const [shouldApplyFees, setShouldApplyFees] = React.useState(() => {
    const stored = localStorage.getItem('applyPlatformFees');
    return stored === 'true';
  });
  
  // Слушаем изменения в localStorage (когда чекбокс меняется в Sidebar)
  React.useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('applyPlatformFees');
      setShouldApplyFees(stored === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('platformFeesChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('platformFeesChanged', handleStorageChange);
    };
  }, []);
  
  // Загружаем данные используя хук
  const widgetData = useWidgetData(
    async (filters, options) => {
      const response = await MetricsService.getLtvByPeriod(filters, groupBy, dayFromInstall, options);
      return response;
    },
    [groupBy, dayFromInstall]
  );
  
  // Используем хелпер для BaseWidget
  const widgetProps = useBaseWidget(widgetData);
  let { data: ltvData } = widgetProps;
  
  // Применяем коэффициенты платформ к InApps данным, если чекбокс включен
  if (shouldApplyFees && ltvData) {
    const { data, totals } = ltvData;
    
    if (data && Array.isArray(data)) {
      // Применяем коэффициенты к каждому периоду
      const adjustedData = data.map(item => {
        const adjustedItem = { ...item };
        
        // Применяем коэффициенты только к InApps, AdMon не трогаем
        if (platform === 'android' || platform === 'all') {
          const androidRevenues = [{ revenue: item.revenueInappsAndroid, platform: 'android' }];
          const adjusted = MetricsService.applyPlatformFees(androidRevenues, true, 'android');
          adjustedItem.revenueInappsAndroid = adjusted[0].revenue;
          adjustedItem.ltvInappsAndroid = item.cohortSize > 0 ? adjusted[0].revenue / item.cohortSize : 0;
        }
        
        if (platform === 'ios' || platform === 'all') {
          const iosRevenues = [{ revenue: item.revenueInappsIos, platform: 'ios' }];
          const adjusted = MetricsService.applyPlatformFees(iosRevenues, true, 'ios');
          adjustedItem.revenueInappsIos = adjusted[0].revenue;
          adjustedItem.ltvInappsIos = item.cohortSize > 0 ? adjusted[0].revenue / item.cohortSize : 0;
        }
        
        // Пересчитываем общий LTV
        const totalRevenue = adjustedItem.revenueInappsAndroid + adjustedItem.revenueInappsIos + 
                            adjustedItem.revenueAdmonAndroid + adjustedItem.revenueAdmonIos;
        adjustedItem.ltvTotal = item.cohortSize > 0 ? totalRevenue / item.cohortSize : 0;
        
        return adjustedItem;
      });
      
      // Пересчитываем итоги
      const totalRevenue = adjustedData.reduce((sum, item) => 
        sum + item.revenueInappsAndroid + item.revenueInappsIos + 
        item.revenueAdmonAndroid + item.revenueAdmonIos, 0
      );
      const avgLtv = totals.totalCohortSize > 0 ? totalRevenue / totals.totalCohortSize : 0;
      
      ltvData = {
        data: adjustedData,
        totals: {
          ...totals,
          avgLtv: Math.round(avgLtv * 100) / 100,
          totalRevenue: Math.round(totalRevenue * 100) / 100
        }
      };
    }
  }
  
  // Компоненты выбора настроек
  const Controls = () => (
    <div className="flex flex-wrap items-center gap-y-2 gap-x-4">
      <ToggleGroup type="single" value={groupBy} onValueChange={(value) => value && setGroupBy(value)}>
        <ToggleGroupItem value="day" aria-label="Daily">
          <Calendar className="h-4 w-4 mr-1" />
          Daily
        </ToggleGroupItem>
        <ToggleGroupItem value="week" aria-label="Weekly">
          <Calendar className="h-4 w-4 mr-1" />
          Weekly
        </ToggleGroupItem>
        <ToggleGroupItem value="month" aria-label="Monthly">
          <Calendar className="h-4 w-4 mr-1" />
          Monthly
        </ToggleGroupItem>
      </ToggleGroup>
      
      <div className="h-6 w-px bg-border" />
      
      <ToggleGroup type="single" value={chartType} onValueChange={(value) => value && setChartType(value)}>
        <ToggleGroupItem value="line" aria-label="Line Chart">
          <LineChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="bar" aria-label="Bar Chart">
          <BarChart3 className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="area" aria-label="Area Chart">
          <AreaChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
  
  return (
    <BaseWidget
      {...widgetProps}
      title="LTV Over Time"
      subtitle={ltvData?.totals ? `Avg LTV: ${MetricsService.formatCurrency(ltvData.totals.avgLtv)}` : null}
      headerAction={<Controls />}
      emptyMessage="No LTV data available"
      skeletonRows={6}
    >
      <div className="space-y-6">
        {/* Выбор дня LTV */}
        <Card>
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">LTV Day</label>
                <span className="text-sm text-muted-foreground">
                  Showing LTV for Day {dayFromInstall}
                </span>
              </div>
              
              {/* Кнопки и поле ввода в одну строку */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Кнопки предустановленных дней как ToggleGroup */}
                <ToggleGroup 
                  type="single" 
                  value={String(dayFromInstall)} 
                  onValueChange={(value) => value && setDayFromInstall(parseInt(value))}
                  className="flex-wrap"
                >
                  {presetDays.map(day => (
                    <ToggleGroupItem 
                      key={day} 
                      value={String(day)}
                      className="px-3 py-1.5 text-sm"
                    >
                      D{day}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
                
                {/* Разделитель */}
                <div className="h-6 w-px bg-border mx-1" />
                
                {/* Поле для произвольного дня */}
                <input
                  type="number"
                  min="0"
                  max="999"
                  value={customDay}
                  onChange={(e) => setCustomDay(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && customDay) {
                      const day = parseInt(customDay);
                      if (!isNaN(day) && day >= 0) {
                        setDayFromInstall(day);
                        setCustomDay('');
                      }
                    }
                  }}
                  onBlur={() => {
                    if (customDay) {
                      const day = parseInt(customDay);
                      if (!isNaN(day) && day >= 0) {
                        setDayFromInstall(day);
                        setCustomDay('');
                      }
                    }
                  }}
                  placeholder="Custom..."
                  className="w-24 px-3 py-1.5 text-sm border rounded-md bg-background [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  style={{ MozAppearance: 'textfield' }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Статистика */}
        {ltvData?.totals && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              icon={Users}
              label="Total Cohort Size"
              value={ltvData.totals.totalCohortSize.toLocaleString()}
              subtitle="Users in period"
            />
            <StatCard
              icon={DollarSign}
              label="Average LTV"
              value={MetricsService.formatCurrency(ltvData.totals.avgLtv)}
              subtitle="Per user"
            />
            <StatCard
              icon={TrendingUp}
              label="Total Revenue"
              value={MetricsService.formatCurrency(ltvData.totals.totalRevenue)}
              subtitle="All sources"
            />
          </div>
        )}
        
        {/* График */}
        {ltvData?.data && ltvData.data.length > 0 && (
          <LtvChart 
            key={`ltv-chart-day${dayFromInstall}-${groupBy}-${ltvData.data.length}`}
            data={ltvData.data} 
            chartType={chartType}
            enabledLines={enabledLines}
            setEnabledLines={setEnabledLines}
            groupBy={groupBy}
          />
        )}
      </div>
    </BaseWidget>
  );
}

export default LtvTimeWidget;
